import { Thermometer, Ruler, Palette, Cog, Award, Truck, Phone, ArrowRight, HandHeart } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_SOFT = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const REASONS = [{
  Icon: Thermometer,
  badge: "Climate-matched",
  title: "We know which model your suburb needs.",
  desc: "Western Sydney needs deeper foam for 45°C summers. Coastal homes need marine-grade hardware. Inner-west needs roller-friendly compact stacks. We won't sell you the wrong door for your postcode."
}, {
  Icon: Ruler,
  badge: "Free in-home measure",
  title: "Accurate quote — measured, not guessed.",
  desc: "Online estimators get within 25%. We come out, measure the opening, check headroom, side clearance, and motor fit. The number we leave with is the number you pay."
}, {
  Icon: Palette,
  badge: "Colour-matched",
  title: "Sample the ColorBond against your home.",
  desc: "Not just stock colours. We bring physical ColorBond chips, hold them against your render / brick / cladding in real Sydney light, and you pick the match. No 'looks lighter on the chart' regrets."
}, {
  Icon: Cog,
  badge: "Honest compatibility check",
  title: "Old motor — kept where it can be.",
  desc: "An insulated door is heavier than a single-skin. Sometimes the existing motor handles it fine. Sometimes it won't. We tell you honestly — no automatic motor-swap upsell."
}, {
  Icon: Award,
  badge: "Specialist installs",
  title: "Hundreds installed, not a side gig.",
  desc: "Insulated panels weigh more, hang differently, and need precise spring tension. We've installed hundreds across Sydney — first-time alignment, no rattles, doors that close flush from year one."
}, {
  Icon: Truck,
  badge: "Old door taken away",
  title: "Clean handover. No skip needed.",
  desc: "We strip the old door, drum, springs, tracks, and motor (if replacing), and take it all to certified metal recycling. You get a new door — and a perfectly clean garage to put it on."
}];
export default function InsulatedWhyUs() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Corner ornaments */}
      <div aria-hidden className="absolute top-10 left-10 pointer-events-none" style={{
      opacity: 0.5
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>
      <div aria-hidden className="absolute bottom-10 right-10 pointer-events-none" style={{
      opacity: 0.5,
      transform: "rotate(180deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD_DARK
          }}>
              06 · Why Choose Gold for Insulated
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: NAVY,
          marginBottom: "20px"
        }}>
            Insulated is a specialist install —
            <br />
            <span style={{
            position: "relative",
            display: "inline-block",
            color: NAVY
          }}>
              and we install specialists
              <span aria-hidden style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: "0.08em",
              height: "0.18em",
              backgroundColor: `${GOLD}55`,
              zIndex: -1
            }} />
            </span>
            .
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: INK_SOFT,
          maxWidth: "640px"
        }}>
            An insulated door is heavier, more expensive, and longer-lasting
            than a single-skin — so the install needs to match. Six things we
            do differently to make sure yours lasts the 15+ years it should.
          </p>
        </div>

        {/* 3x2 grid with left gold accent */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {REASONS.map(({
          Icon,
          badge,
          title,
          desc
        }, i) => <article key={title} className="relative rounded-2xl overflow-hidden transition-all hover:translate-y-[-2px] hover:shadow-xl" style={{
          backgroundColor: "#ffffff",
          border: `1px solid rgba(8,20,56,0.08)`,
          boxShadow: "0 4px 14px rgba(8,20,56,0.05)"
        }}>
              {/* Left gold accent bar */}
              <div aria-hidden className="absolute top-0 bottom-0 left-0" style={{
            width: "4px",
            background: `linear-gradient(180deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`
          }} />

              {/* Subtle top-right corner gradient */}
              <div aria-hidden className="absolute pointer-events-none" style={{
            top: 0,
            right: 0,
            width: "80px",
            height: "80px",
            background: `linear-gradient(225deg, ${GOLD}1A 0%, transparent 60%)`
          }} />

              <div className="p-7 md:p-8 pl-8 md:pl-9 relative">
                {/* Number + Icon row */}
                <div className="flex items-start gap-4 mb-5">
                  <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "44px",
                fontWeight: 900,
                color: GOLD,
                lineHeight: 0.9,
                letterSpacing: "-0.04em",
                minWidth: "60px"
              }}>
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg flex-shrink-0" style={{
                width: 44,
                height: 44,
                backgroundColor: `${GOLD}15`,
                border: `1px solid ${GOLD}33`
              }}>
                    <Icon size={20} color={GOLD_DARK} strokeWidth={2.25} />
                  </span>
                </div>

                {/* Badge */}
                <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: GOLD_DARK,
              marginBottom: "8px"
            }}>
                  {badge}
                </div>

                {/* Title */}
                <h3 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "1.2rem",
              lineHeight: 1.25,
              color: NAVY,
              letterSpacing: "-0.015em",
              marginBottom: "12px"
            }}>
                  {title}
                </h3>

                {/* Desc */}
                <p style={{
              fontFamily: FONT_BODY,
              fontSize: "14px",
              lineHeight: 1.65,
              color: INK_SOFT
            }}>
                  {desc}
                </p>
              </div>
            </article>)}
        </div>

        {/* Bottom signature row */}
        <div className="mt-12 rounded-xl p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-5" style={{
        backgroundColor: CREAM_DEEP,
        border: `1px dashed ${GOLD}55`
      }}>
          <div className="flex items-start gap-4">
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg flex-shrink-0" style={{
            width: 44,
            height: 44,
            backgroundColor: `${GOLD}22`,
            border: `1px solid ${GOLD}44`
          }}>
              <HandHeart size={20} color={GOLD_DARK} strokeWidth={2.5} />
            </span>
            <div>
              <h4 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "1.05rem",
              color: NAVY,
              marginBottom: "4px",
              letterSpacing: "-0.01em"
            }}>
                A door that should last 15+ years deserves an install that does
                too.
              </h4>
              <p style={{
              fontFamily: FONT_BODY,
              fontSize: "13.5px",
              color: INK_SOFT,
              lineHeight: 1.55,
              maxWidth: "560px"
            }}>
                Family-run since 2009 · Bass Hill, NSW · 1-Year Labour
                Warranty included on every install.
              </p>
            </div>
          </div>

          <a href="tel:0485000933" className="inline-flex items-center gap-2 px-5 py-3 rounded-full transition-all hover:scale-[1.02] flex-shrink-0" style={{
          backgroundColor: NAVY,
          color: "#ffffff",
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: "13px",
          boxShadow: `0 6px 18px ${NAVY}33`
        }}>
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <Phone size={14} strokeWidth={2.5} />
            </span>
            Book a free measure
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <ArrowRight size={13} strokeWidth={2.5} />
            </span>
          </a>
        </div>
      </div>
    </section>;
}